import { useEffect, useMemo, useState } from 'react';
import { Modal, TextInput, Stack, Table, Paper, Button, Center, Loader, Text, Group } from '@mantine/core';
import { IconScope as IconSearch } from '../../components/icons/CncIcons';
import { api, imageUrl } from '../../api/client';

interface ConsumableListItem {
  id: number;
  name: string;
  imagePath: string | null;
  typicalPriceEur: number | null;
  distributors?: { name: string }[];
}

export default function SelectConsumableModal({
  equipmentId,
  attachedIds,
  onClose,
  onDone,
}: {
  equipmentId: number;
  attachedIds: number[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [all, setAll] = useState<ConsumableListItem[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);

  useEffect(() => {
    (async () => {
      const list = await api.get<ConsumableListItem[]>('/consumables');
      // Fetch distributors for each — small N, still cheap. Single round-trip per item.
      const withDist = await Promise.all(list.map(async (c) => {
        try {
          const full = await api.get<{ distributors: { name: string }[] }>(`/consumables/${c.id}`);
          return { ...c, distributors: full.distributors };
        } catch {
          return { ...c, distributors: [] };
        }
      }));
      setAll(withDist);
      setLoading(false);
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((c) =>
      c.name.toLowerCase().includes(q)
      || (c.distributors ?? []).some((d) => d.name.toLowerCase().includes(q))
    );
  }, [all, search]);

  async function attach(consId: number) {
    setBusyId(consId);
    try {
      await api.post(`/equipment/${equipmentId}/consumables/${consId}`);
      onDone();
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Modal opened onClose={onClose} title="Select Consumable" size="lg">
      <Stack>
        <TextInput
          leftSection={<IconSearch size={14} />}
          placeholder="Search by consumable name or distributor…"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          autoFocus
        />
        {loading ? (
          <Center p="xl"><Loader /></Center>
        ) : (
          <Paper withBorder style={{ maxHeight: 400, overflowY: 'auto' }}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 60 }}>Image</Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Distributors</Table.Th>
                  <Table.Th>Price</Table.Th>
                  <Table.Th style={{ width: 100 }}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filtered.map((c) => {
                  const attached = attachedIds.includes(c.id);
                  return (
                    <Table.Tr key={c.id}>
                      <Table.Td>
                        <div className="image-box">{c.imagePath && <img src={imageUrl(c.imagePath, true)} alt={c.name} />}</div>
                      </Table.Td>
                      <Table.Td>{c.name}</Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed">{(c.distributors ?? []).map((d) => d.name).join(', ') || '—'}</Text>
                      </Table.Td>
                      <Table.Td>{c.typicalPriceEur != null ? `€ ${c.typicalPriceEur.toFixed(2)}` : '—'}</Table.Td>
                      <Table.Td>
                        <Button size="xs" disabled={attached} loading={busyId === c.id} onClick={() => attach(c.id)}>
                          {attached ? 'Added' : 'Add'}
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
                {filtered.length === 0 && (
                  <Table.Tr><Table.Td colSpan={5}><Text c="dimmed" ta="center" size="sm">No matches.</Text></Table.Td></Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Paper>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Close</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
